import Tabs from '@components/V4/Tabs/Tabs';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useCalendar from '../../../hooks/useCalendar';
import EducationStrategyTab from './EducationStrategyTab';
import OverviewTab from './OverviewTab';
import QualityScoresTab from './QualityScoresTab';
import ReschedulesTab from './ReschedulesTab';
import TimingsTab from './TimingsTab';
import { TARGET_DATE_FORMAT } from './common';

type ReportingTab =
  | 'overview'
  | 'timings'
  | 'reschedules'
  | 'education-strategy'
  | 'quality-scores';

const TABS: { label: string; value: ReportingTab; description: string }[] = [
  {
    label: 'Overview',
    value: 'overview',
    description: 'Headline operational metrics for the selected month.',
  },
  {
    label: 'Timings',
    value: 'timings',
    description: 'How fast students move from payment to their first session.',
  },
  {
    label: 'Reschedules',
    value: 'reschedules',
    description: 'Who reschedules, how often, and why.',
  },
  {
    label: 'Education Strategy',
    value: 'education-strategy',
    description: 'Onboarding and education strategy meetings, and strategist workload.',
  },
  {
    label: 'Quality Scores',
    value: 'quality-scores',
    description: 'AI-graded mentor quality scores per session, linked to the graded meeting.',
  },
];

const MONTHLESS_TABS: ReportingTab[] = [];

const isReportingTab = (value: string | null): value is ReportingTab =>
  TABS.some((tab) => tab.value === value);

const Reporting = () => {
  const location = useLocation();
  const history = useHistory();

  const initialParams = new URLSearchParams(location.search);
  const initialTab = initialParams.get('tab');
  const initialMonth = initialParams.get('selectedDate');

  const [currentTab, setCurrentTab] = useState<ReportingTab>(
    isReportingTab(initialTab) ? initialTab : 'overview'
  );
  const [selectedDate, setSelectedDate] = useState(
    initialMonth && moment(initialMonth, TARGET_DATE_FORMAT, true).isValid()
      ? initialMonth
      : moment().format(TARGET_DATE_FORMAT)
  );

  const { goForward, goBackward, selected } = useCalendar(moment(selectedDate, TARGET_DATE_FORMAT));

  useEffect(() => {
    setSelectedDate(selected.format(TARGET_DATE_FORMAT));
  }, [selected]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('selectedDate') === selectedDate && params.get('tab') === currentTab) return;

    params.set('selectedDate', selectedDate);
    params.set('tab', currentTab);
    history.replace(`${location.pathname}?${params.toString()}`);
  }, [selectedDate, currentTab, location.search, location.pathname, history]);

  const renderContent = (tab: ReportingTab) => {
    switch (tab) {
      case 'overview':
        return (
          <OverviewTab
            month={selectedDate}
            onRescheduleDrilldown={() => setCurrentTab('reschedules')}
          />
        );
      case 'timings':
        return <TimingsTab month={selectedDate} />;
      case 'reschedules':
        return <ReschedulesTab month={selectedDate} />;
      case 'education-strategy':
        return <EducationStrategyTab month={selectedDate} />;
      case 'quality-scores':
        return <QualityScoresTab month={selectedDate} />;
      default:
        return null;
    }
  };

  const activeTab = TABS.find((tab) => tab.value === currentTab);
  const isMonthScoped = !MONTHLESS_TABS.includes(currentTab);

  return (
    <section className="min-h-[calc(100vh-74px)] bg-surfaceBackground p-6 font-raleway text-white tablet:p-8">
      <div className="mb-8 flex flex-col gap-4 tablet:flex-row tablet:items-start tablet:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reporting</h1>
          <p className="mt-1 text-sm text-customGrey">{activeTab?.description}</p>
        </div>
        {isMonthScoped ? (
          <div className="flex items-center gap-1 self-start rounded-full bg-surfaceObject p-1">
            <button
              type="button"
              onClick={goBackward}
              aria-label="Previous month"
              className="flex h-9 w-9 items-center justify-center rounded-full text-customGrey transition-colors hover:bg-surfaceHover hover:text-white"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="min-w-[150px] text-center text-sm font-semibold">
              {selected.format('MMMM YYYY')}
            </span>
            <button
              type="button"
              onClick={goForward}
              aria-label="Next month"
              className="flex h-9 w-9 items-center justify-center rounded-full text-customGrey transition-colors hover:bg-surfaceHover hover:text-white"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        ) : null}
      </div>

      <Tabs
        tabs={TABS}
        activeTab={currentTab}
        onTabChange={setCurrentTab}
        renderContent={renderContent}
        contentWrapperClassName="mt-6 flex flex-col gap-8"
      />
    </section>
  );
};

export default Reporting;
